import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Chip,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
   PictureAsPdf,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import formularioService from '../../services/formularioService';
import especialidadService from '../../services/especialidadService';

/**
 * Componente para administrar formularios y su asignación a especialidades
 * @returns {JSX.Element} Componente React
 */
const AdministrarFormularios = () => {
  // Estados
  const [formularios, setFormularios] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [tabActiva, setTabActiva] = useState(0);
  const [dialogoFormulario, setDialogoFormulario] = useState(false);
  const [dialogoAsignacion, setDialogoAsignacion] = useState(false);
  const [formularioSeleccionado, setFormularioSeleccionado] = useState(null);
  const [especialidadesSeleccionadas, setEspecialidadesSeleccionadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Estado del formulario en edición
  const [formularioForm, setFormularioForm] = useState({
    nombre: '',
    descripcion: '',
    estructura: JSON.stringify({
      campos: [],
      secciones: []
    }),
    especialidad_id: '',
    activo: true
  });
  
  const navigate = useNavigate();
  
  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        
        // Cargar formularios - Aquí está el problema
        const respFormularios = await formularioService.getFormularios();
         
        setFormularios(respFormularios || []);
        
        // Cargar especialidades
        const respEspecialidades = await especialidadService.getEspecialidades();
        setEspecialidades(respEspecialidades.data || []);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('Error al cargar datos. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    
    cargarDatos();
  }, []);
  
  // Manejar cambio de tab
  const handleTabChange = (event, newValue) => {
    setTabActiva(newValue);
  };
  
  // Abrir diálogo para nuevo formulario
  const handleNuevoFormulario = () => {
    setFormularioForm({
      nombre: '',
      descripcion: '',
      estructura: JSON.stringify({
        campos: [],
        secciones: []
      }),
      especialidad_id: '',
      activo: true
    });
    setFormularioSeleccionado(null);
    setDialogoFormulario(true);
  };
  
  const handleNuevoFormularioPDF = () => {
  // Primero crear un formulario básico
  setFormularioForm({
    nombre: '',
    descripcion: '',
    estructura: JSON.stringify({
      campos: [],
      secciones: []
    }),
    especialidad_id: '',
    activo: true
  });
  
  // Abrir el diálogo
  setFormularioSeleccionado(null);
  setDialogoFormulario(true);
};

  // Abrir diálogo para editar formulario
  const handleEditarFormulario = (formulario) => {
    setFormularioForm({
      nombre: formulario.nombre,
      descripcion: formulario.descripcion || '',
      estructura: typeof formulario.estructura === 'string' 
                ? formulario.estructura 
                : JSON.stringify(formulario.estructura),
      especialidad_id: formulario.especialidad_id || '',
      activo: formulario.activo
    });
    setFormularioSeleccionado(formulario);
    setDialogoFormulario(true);
  };
  
  // Abrir diálogo para asignar formulario a especialidades
  const handleAsignarFormulario = (formulario) => {
    setFormularioSeleccionado(formulario);
    
    // Obtener especialidades asignadas actualmente
    const especialidadesActuales = formulario.especialidades 
      ? formulario.especialidades.map(esp => esp.id)
      : formulario.especialidad_id 
        ? [formulario.especialidad_id] 
        : [];
    
    setEspecialidadesSeleccionadas(especialidadesActuales);
    setDialogoAsignacion(true);
  };
  
  // Manejar cambio en el formulario de edición
  const handleFormularioChange = (campo, valor) => {
    setFormularioForm(prev => ({
      ...prev,
      [campo]: valor
    }));
  };
  
  // Manejar cambio en selección de especialidades
  const handleEspecialidadToggle = (especialidadId) => {
    setEspecialidadesSeleccionadas(prev => {
      if (prev.includes(especialidadId)) {
        return prev.filter(id => id !== especialidadId);
      } else {
        return [...prev, especialidadId];
      }
    });
  };
  
  
  // Guardar asignación de especialidades
  const handleGuardarAsignacion = async () => {
    try {
      setSubmitting(true);
      
      // Actualizar asignación de especialidades
      await formularioService.asignarEspecialidades(
        formularioSeleccionado.id,
        especialidadesSeleccionadas
      );
      
      // Recargar formularios
      const respFormularios = await formularioService.getFormularios();
      setFormularios(respFormularios.data || []);
      
      setDialogoAsignacion(false);
    } catch (error) {
      console.error('Error al asignar especialidades:', error);
      setError('Error al asignar especialidades. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Eliminar formulario
  const handleEliminarFormulario = async (formulario) => {
    // Confirmación
    if (!window.confirm(`¿Está seguro que desea eliminar el formulario "${formulario.nombre}"?`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Eliminar formulario
      await formularioService.deleteFormulario(formulario.id);
      
      // Recargar formularios
      const respFormularios = await formularioService.getFormularios();
      setFormularios(respFormularios.data || []);
    } catch (error) {
      console.error('Error al eliminar formulario:', error);
      setError('Error al eliminar formulario. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };
  
  //const handleNuevoFomularioPDF = () => {
  // Primero crear un formulario básico
  //setFormularioForm({
  //  nombre: '',
  //  descripcion: '',
  //  estructura: JSON.stringify({
  //    campos: [],
  //    secciones: []
  //  }),
  //  especialidad_id: '',
  //  activo: true
  //});
  
  // Abrir el diálogo
  //setFormularioSeleccionado(null);
  //setDialogoFormulario(true);
  
  // Una vez guardado, navegar a la configuración de PDF
  // Esto se manejará en handleGuardarFormulario
//};

// Modificar la función handleGuardarFormulario para navegar a la configuración PDF
const handleGuardarFormulario = async () => {
  try {
    setSubmitting(true);
    
    // Validar campos requeridos
    if (!formularioForm.nombre) {
      setError('El nombre del formulario es obligatorio');
      return;
    }
    
    let resultado;
    
    if (formularioSeleccionado) {
      // Actualizar formulario existente
      resultado = await formularioService.updateFormulario(
        formularioSeleccionado.id,
        formularioForm
      );
    } else {
      // Crear nuevo formulario
      resultado = await formularioService.createFormulario(formularioForm);
    }
    
    // Recargar formularios
    const respFormularios = await formularioService.getFormularios();
    setFormularios(respFormularios.data || []);
    
    setDialogoFormulario(false);
    
    // Si venimos de la función handleNuevoFormularioPDF, navegamos a la configuración
    if (tabActiva === 2 && !formularioSeleccionado && resultado.id) {
      navigate(`/configuracion/formularios/${resultado.id}/pdf`);
    }
  } catch (error) {
    console.error('Error al guardar formulario:', error);
    setError('Error al guardar formulario. Por favor, intente nuevamente.');
  } finally {
    setSubmitting(false);
  }
};

  // Abrir el editor de formularios
  const handleEditarEstructura = (formulario) => {
    navigate(`/configuracion/formularios/${formulario.id}/editor`);
  };
  
  // Vista previa de formulario
  const handleVistaPrevia = (formulario) => {
    window.open(`/formularios/preview/${formulario.id}`, '_blank');
  };
  
  // Renderizar tabla de formularios
  const renderTablaFormularios = () => {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Especialidad</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell width="200">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formularios.map(formulario => (
              <TableRow key={formulario.id}>
                <TableCell>{formulario.nombre}</TableCell>
                <TableCell>{formulario.descripcion}</TableCell>
                <TableCell>
                  {formulario.especialidad ? formulario.especialidad.nombre : '-'}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={formulario.activo ? 'Activo' : 'Inactivo'} 
                    color={formulario.activo ? 'success' : 'default'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    <Tooltip title="Editar información">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditarFormulario(formulario)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver formulario">
                      <IconButton 
                        size="small" 
                        onClick={() => navigate(`/formularios/preview/${formulario.id}`)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Configurar PDF">
                      <IconButton 
                        size="small" 
                        onClick={() => navigate(`/configuracion/formularios/${formulario.id}/pdf`)}
                        color="secondary"
                      >
                        <PictureAsPdf fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar estructura">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditarEstructura(formulario)}
                        color="secondary"
                      >
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Asignar a especialidades">
                      <IconButton 
                        size="small" 
                        onClick={() => handleAsignarFormulario(formulario)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Vista previa">
                      <IconButton 
                        size="small" 
                        onClick={() => handleVistaPrevia(formulario)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Eliminar">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEliminarFormulario(formulario)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Configurar PDF">
                      <IconButton 
                        size="small" 
                        onClick={() => navigate(`/configuracion/formularios/${formulario.id}/pdf`)}
                        color="secondary"
                      >
                        <PictureAsPdf fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Renderizar vista de especialidades
  const renderTablaEspecialidades = () => {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Especialidad</TableCell>
              <TableCell>Formularios Asignados</TableCell>
              <TableCell width="100">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {especialidades.map(especialidad => {
              // Filtrar formularios para esta especialidad
              const formulariosEspecialidad = formularios.filter(form => 
                form.especialidad_id === especialidad.id ||
                (form.especialidades && form.especialidades.some(esp => esp.id === especialidad.id))
              );
              
              return (
                <TableRow key={especialidad.id}>
                  <TableCell>{especialidad.nombre}</TableCell>
                  <TableCell>
                    {formulariosEspecialidad.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No hay formularios asignados
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {formulariosEspecialidad.map(form => (
                          <Chip 
                            key={form.id} 
                            label={form.nombre} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => {/* Implementar vista de formularios por especialidad */}}
                    >
                      Ver
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Si está cargando, mostrar indicador
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Administrar Formularios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNuevoFormulario}
        >
          Nuevo Formulario
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabActiva} onChange={handleTabChange}>
          <Tab label="Formularios" />
          <Tab label="Por Especialidad" />
          <Tab label="Plantillas PDF" />
        </Tabs>
        <Divider />
        <Box sx={{ p: 2 }}>
          {tabActiva === 2 && (
            <Box p={2}>
              <Typography variant="h6" gutterBottom>
                Configuración de Plantillas PDF
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNuevoFormularioPDF}
                startIcon={<AddIcon />}
                sx={{ mb: 3 }}
              >
                Nuevo formulario PDF
              </Button>
              
              {/* Lista de formularios con indicador de PDF */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Especialidad</TableCell>
                      <TableCell>Estado PDF</TableCell>
                      <TableCell width="200">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formularios.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No hay formularios disponibles
                        </TableCell>
                      </TableRow>
                    ) : (
                      formularios.map(formulario => (
                        <TableRow key={formulario.id}>
                          <TableCell>{formulario.nombre}</TableCell>
                          <TableCell>
                            {formulario.especialidad ? formulario.especialidad.nombre : '-'}
                          </TableCell>
                          <TableCell>
                            {formulario.plantilla_pdf ? (
                              <Chip 
                                label="PDF Configurado" 
                                color="success" 
                                size="small" 
                                icon={<CheckCircleIcon />}
                              />
                            ) : (
                              <Chip 
                                label="Sin PDF" 
                                color="default" 
                                size="small" 
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => navigate(`/configuracion/formularios/${formulario.id}/pdf`)}
                              sx={{ mr: 1 }}
                            >
                              Configurar PDF
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/formularios/preview/${formulario.id}`)}
                            >
                              Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {tabActiva === 0 ? renderTablaFormularios() : renderTablaEspecialidades()}
        </Box>
      </Paper>
      
      {/* Diálogo para crear/editar formulario */}
      <Dialog
        open={dialogoFormulario}
        onClose={() => !submitting && setDialogoFormulario(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {formularioSeleccionado ? 'Editar Formulario' : 'Nuevo Formulario'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={formularioForm.nombre}
                onChange={(e) => handleFormularioChange('nombre', e.target.value)}
                required
                disabled={submitting}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={formularioForm.descripcion}
                onChange={(e) => handleFormularioChange('descripcion', e.target.value)}
                multiline
                rows={3}
                disabled={submitting}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Especialidad Principal</InputLabel>
                <Select
                  value={formularioForm.especialidad_id}
                  onChange={(e) => handleFormularioChange('especialidad_id', e.target.value)}
                  label="Especialidad Principal"
                  disabled={submitting}
                >
                  <MenuItem value="">Sin especialidad</MenuItem>
                  {especialidades.map(especialidad => (
                    <MenuItem key={especialidad.id} value={especialidad.id}>
                      {especialidad.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formularioForm.activo}
                    onChange={(e) => handleFormularioChange('activo', e.target.checked)}
                    disabled={submitting}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDialogoFormulario(false)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={24} /> : <SaveIcon />}
            onClick={handleGuardarFormulario}
            disabled={submitting}
          >
            {submitting ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo para asignar formulario a especialidades */}
      <Dialog
        open={dialogoAsignacion}
        onClose={() => !submitting && setDialogoAsignacion(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Asignar Formulario a Especialidades
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            {formularioSeleccionado?.nombre}
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            Seleccione las especialidades:
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            {especialidades.map(especialidad => (
              <FormControlLabel
                key={especialidad.id}
                control={
                  <Checkbox
                    checked={especialidadesSeleccionadas.includes(especialidad.id)}
                    onChange={() => handleEspecialidadToggle(especialidad.id)}
                    disabled={submitting}
                  />
                }
                label={especialidad.nombre}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDialogoAsignacion(false)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={24} /> : <SaveIcon />}
            onClick={handleGuardarAsignacion}
            disabled={submitting}
          >
            {submitting ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdministrarFormularios;